import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/shared/PageHeader';
import { LoadingState } from '../../components/shared/LoadingState';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { useSignatureEnvelopes } from '../../core/hooks/useSignatureEnvelopes';
import { useClients } from '../../core/hooks/useClients';
import { useOrganizations } from '../../core/hooks/useOrganizations';
import { useUsers } from '../../core/hooks/useUsers';
import { useDocuments } from '../../core/hooks/useDocuments';
import { useAuth } from '../../core/contexts/AuthContext';
import { FileUpload } from '../../components/shared/FileUpload';
import { UserRole } from '../../core/models/User';
import { findClientAccountForProject, resolveProjectContactEmail } from '../../core/utils/projectContact';
import { logError } from '../../lib/utils';

export const EnvelopeCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, organizationId } = useAuth();
  const { createEnvelope, loading: creating } = useSignatureEnvelopes();
  const { clients, loading: clientsLoading } = useClients(organizationId);
  const { organizations } = useOrganizations();
  const { users, loading: usersLoading } = useUsers(organizationId);
  const { documents, loading: documentsLoading } = useDocuments(null, organizationId);

  const [sourceType, setSourceType] = useState<'pdf_upload' | 'document_template'>('pdf_upload');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [clientEmail, setClientEmail] = useState<string>('');
  const [clientName, setClientName] = useState<string>('');
  const [companySignerUserId, setCompanySignerUserId] = useState<string>(currentUser?.uid || '');

  // Auto-fill client info when client is selected
  React.useEffect(() => {
    if (selectedClientId) {
      const client = clients.find(c => c.id === selectedClientId);
      if (client) {
        const clientAccount = findClientAccountForProject(client, organizations);
        setClientEmail(resolveProjectContactEmail(client, clientAccount));
        setClientName(client.name || '');
      }
    }
  }, [selectedClientId, clients, organizations]);

  // Auto-fill client info when document is selected
  React.useEffect(() => {
    if (selectedDocumentId && sourceType === 'document_template') {
      const document = documents.find(d => d.id === selectedDocumentId);
      if (document) {
        setSelectedClientId(document.clientId);
        const client = clients.find(c => c.id === document.clientId);
        if (client) {
          const clientAccount = findClientAccountForProject(client, organizations);
          setClientEmail(resolveProjectContactEmail(client, clientAccount));
          setClientName(client.name || '');
        }
      }
    }
  }, [selectedDocumentId, sourceType, documents, clients, organizations]);

  const handleFileUpload = (file: File) => {
    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      return;
    }
    setPdfFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      alert('You must be logged in to create an envelope.');
      return;
    }

    if (!selectedClientId) {
      alert('Please select a project.');
      return;
    }

    if (!clientEmail) {
      alert('Please enter client email.');
      return;
    }

    if (!companySignerUserId) {
      alert('Please select a company signer.');
      return;
    }

    // Determine effective organization for this envelope:
    // - For regular users: must have organizationId from auth
    // - For SUPER_ADMIN without organizationId: derive from selected client
    const selectedClient = clients.find(c => c.id === selectedClientId);
    const effectiveOrganizationId =
      organizationId || (currentUser.role === UserRole.SUPER_ADMIN ? selectedClient?.organizationId : undefined);

    if (!effectiveOrganizationId) {
      alert(
        'Client ID is required. Please ensure you are logged in with a valid client account or select a project associated with a client account.'
      );
      return;
    }

    if (sourceType === 'pdf_upload' && !pdfFile) {
      alert('Please upload a PDF file.');
      return;
    }

    if (sourceType === 'document_template' && !selectedDocumentId) {
      alert('Please select a document.');
      return;
    }

    try {
      let pdfBase64: string | undefined;
      if (sourceType === 'pdf_upload' && pdfFile) {
        const reader = new FileReader();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result);
          };
          reader.onerror = reject;
          reader.readAsDataURL(pdfFile);
        });
        pdfBase64 = dataUrl.replace(/^data:application\/pdf;base64,/, '');
      }

      const envelopeId = await createEnvelope({
        pdfFile: pdfBase64,
        documentId: sourceType === 'document_template' ? selectedDocumentId : undefined,
        clientId: selectedClientId,
        organizationId: effectiveOrganizationId,
        clientEmail,
        clientName,
        companySignerUserId,
      });

      navigate(`/envelopes/${envelopeId}`);
    } catch (err: any) {
      logError('signatures', err, { source: 'EnvelopeCreatePage.handleSubmit' });
      let errorMessage = 'Failed to create envelope. Please try again.';
      if (err.message) {
        if (err.message.includes('Missing required fields')) {
          errorMessage = 'Please fill in all required fields.';
        } else if (err.message.includes('not found')) {
          errorMessage = 'One or more selected items were not found. Please try again.';
        } else {
          errorMessage = err.message;
        }
      }
      alert(errorMessage);
    }
  };

  if (clientsLoading || usersLoading || documentsLoading) {
    return <LoadingState message="Loading..." />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Create Signature Envelope"
        description="Create a new signature workflow for a document"
        icon={FileText}
        actions={
          <Button variant="outline" onClick={() => navigate('/envelopes')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        }
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Source Selection</CardTitle>
            <CardDescription>Choose how to create the envelope</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={sourceType} onValueChange={(v) => setSourceType(v as 'pdf_upload' | 'document_template')}>
              <TabsList>
                <TabsTrigger value="pdf_upload">
                  <Upload className="mr-2 h-4 w-4" />
                  Upload PDF
                </TabsTrigger>
                <TabsTrigger value="document_template">
                  <FileText className="mr-2 h-4 w-4" />
                  Use Document Template
                </TabsTrigger>
              </TabsList>

              <TabsContent value="pdf_upload" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Upload PDF File</label>
                  <p className="text-sm text-gray-500">Select a PDF file to create a signature envelope</p>
                  <FileUpload
                    accept=".pdf"
                    onFileSelect={handleFileUpload}
                  />
                </div>
                {pdfFile && (
                  <div className="text-sm text-gray-600">
                    Selected: {pdfFile.name} ({(pdfFile.size / 1024).toFixed(2)} KB)
                  </div>
                )}
              </TabsContent>

              <TabsContent value="document_template" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Document</label>
                  <Select value={selectedDocumentId} onValueChange={setSelectedDocumentId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a document" />
                    </SelectTrigger>
                    <SelectContent>
                      {documents
                        .filter(d => d.status === 'draft' || d.status === 'completed')
                        .map((document) => (
                          <SelectItem key={document.id} value={document.id}>
                            {document.title} - {clients.find(c => c.id === document.clientId)?.name || 'Unknown Project'}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {documents.length === 0 && (
                    <p className="text-sm text-gray-500">No documents available. Create a document first.</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Project Information</CardTitle>
            <CardDescription>Enter the client contact details for signing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Project *</label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Input
              label="Client Email *"
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              required
              placeholder="project@example.com"
            />

            <Input
              label="Project Name"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Project name"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Company Signer</CardTitle>
            <CardDescription>Select who will sign on behalf of the company</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <label className="text-sm font-medium">Company Signer *</label>
              <Select value={companySignerUserId} onValueChange={setCompanySignerUserId} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select a company signer" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.uid} value={user.uid}>
                      {user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.displayName || user.email || 'Unknown User'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/envelopes')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={creating}>
            {creating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Envelope'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};
